import { useCallback, useMemo, useRef, useState } from 'react';

/** Per-workspace key, like `left-panel:<ws>`. */
function storageKeyFor(workspaceId: string | undefined): string | undefined {
  return workspaceId ? `workspace-sidebar:${workspaceId}` : undefined;
}

/**
 * Read one workspace's expanded folders. The stored value is untrusted (hand
 * edited, half written, or from an older build), so anything that is not a
 * list of strings reads as nothing expanded.
 */
function readExpanded(workspaceId: string | undefined): ReadonlySet<string> {
  const key = storageKeyFor(workspaceId);
  if (!key || typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    const expanded = (parsed as { expanded?: unknown } | null)?.expanded;
    return Array.isArray(expanded)
      ? new Set(expanded.filter((id): id is string => typeof id === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function writeExpanded(workspaceId: string | undefined, expanded: ReadonlySet<string>): void {
  const key = storageKeyFor(workspaceId);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify({ expanded: [...expanded] }));
  } catch {
    // Storage full or disabled: expansion still works for this session.
  }
}

export interface SidebarExpanded {
  /** What renders open: the user's folders plus this session's revealed ones. */
  readonly expanded: ReadonlySet<string>;
  /**
   * The user opens or closes one folder. Only the user's own expanded folders
   * persist (written by the setter, never an effect); closing a revealed
   * folder closes it for this session too.
   */
  readonly setOpen: (id: string, isOpen: boolean) => void;
  /** Adds ids for this session only (revealing the route's folder is not a preference). */
  readonly reveal: (ids: ReadonlyArray<string>) => void;
}

const NONE: ReadonlySet<string> = new Set();

/**
 * The sidebar tree's expanded folders, persisted per workspace.
 *
 * Written by the SETTER, never by an effect on state: an effect keyed on the
 * workspace would write the previous workspace's set under the new key on
 * every switch (the bleed `useLeftPanel` documents).
 * A workspace switch reloads that workspace's own set during render.
 */
export function useSidebarExpanded(workspaceId: string | undefined): SidebarExpanded {
  const [manual, setManual] = useState(() => readExpanded(workspaceId));
  const [revealed, setRevealed] = useState(NONE);
  const loadedForRef = useRef(workspaceId);
  if (loadedForRef.current !== workspaceId) {
    loadedForRef.current = workspaceId;
    setManual(readExpanded(workspaceId));
    setRevealed(NONE);
  }

  const setOpen = useCallback(
    (id: string, isOpen: boolean) => {
      const next = new Set(manual);
      if (isOpen) next.add(id);
      else next.delete(id);
      setManual(next);
      writeExpanded(workspaceId, next);
      if (!isOpen) {
        setRevealed((prev) => {
          if (!prev.has(id)) return prev;
          const rest = new Set(prev);
          rest.delete(id);
          return rest;
        });
      }
    },
    [manual, workspaceId],
  );

  const reveal = useCallback((ids: ReadonlyArray<string>) => {
    setRevealed((prev) => (ids.every((id) => prev.has(id)) ? prev : new Set([...prev, ...ids])));
  }, []);

  const expanded = useMemo(
    () => (revealed.size === 0 ? manual : new Set([...manual, ...revealed])),
    [manual, revealed],
  );

  return { expanded, setOpen, reveal };
}
